import { Alert, Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getMainSettings } from 'src/shared/services/regSettingService';
import { RegSettingDetail } from 'src/settings/models/regSetting';
import EmailIcon from '@mui/icons-material/Email';

type OnboardingStepPaidEmailProps = {
  onNavigateAway?: () => void;
};

export const OnboardingStepPaidEmail = ({ onNavigateAway }: OnboardingStepPaidEmailProps) => {
  const { t } = useTranslation(['onboarding']);
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMainSettings();
      const settings: RegSettingDetail[] = res.data;
      const subjectSetting = settings.find((s) => s.keyName === 'subjectNewMember');
      const bodySetting = settings.find((s) => s.keyName === 'bodyNewMember');
      if (isMounted()) {
        setSubject(subjectSetting?.varchar || '');
        setBody(bodySetting?.text || '');
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.paidEmail.description')}
            </Typography>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.paidEmail.hint')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.paidEmail.previewTitle')}
          </Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.paidEmail.editHintPrefix')}{' '}
              <Button
                variant='text'
                size='small'
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                }}
                onClick={() => {
                  if (onNavigateAway) onNavigateAway();
                }}
              >
                {t('onboarding:stepContent.paidEmail.editHintLink')}
              </Button>
              . {t('onboarding:stepContent.paidEmail.editHintSuffix')}
            </Alert>
            {loading ? (
              <Skeleton variant='rounded' height={150} />
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
                    {t('onboarding:stepContent.paidEmail.emailPreview')}
                  </Typography>
                  <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Mail header */}
                    <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5, borderBottom: 1, borderColor: 'grey.300' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <EmailIcon fontSize='small' color='action' />
                        <Typography variant='caption' fontWeight={600} color='text.secondary'>
                          {t('onboarding:stepContent.paidEmail.from')}: {currentUser?.customerName || ''}
                        </Typography>
                      </Box>
                      <Typography variant='body2' fontWeight={600}>
                        {subject || t('onboarding:stepContent.paidEmail.noSubject')}
                      </Typography>
                    </Box>
                    {/* Mail body */}
                    <Box sx={{ px: 2, py: 2, bgcolor: '#fff' }}>
                      {body ? (
                        <Typography variant='body2' color='text.secondary' component='div' dangerouslySetInnerHTML={{ __html: body }} />
                      ) : (
                        <Typography variant='body2' color='text.disabled' sx={{ fontStyle: 'italic' }}>
                          {t('onboarding:stepContent.paidEmail.noMailText')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
